import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faWrench,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import SearchableAddDropdown from "../components/SearchableAddDropdown";

const SYSTEM_LIST_KEYS = {
  applicationType: "application_type",
};

const FieldApplicationLaborManagement = () => {
  const [laborMethods, setLaborMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceYear, setCopySourceYear] = useState(null);
  const [copyTargetYear, setCopyTargetYear] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);
  const [formData, setFormData] = useState({
    application_type: "",
    cost_per_unit: "",
    unit_of_measure: "sqm",
    year: new Date().getFullYear(),
    currency: "CAD",
    is_active: true,
  });
  const [systemSettings, setSystemSettings] = useState({
    defaultCurrency: "CAD",
    preferredUnit: "sqm",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [applicationTypeOptions, setApplicationTypeOptions] = useState([]);

  const fetchLaborMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/field-application-labor?limit=500");
      if (response.data.success) {
        const rows = response.data.data || [];
        setLaborMethods(rows);
        setExpandedYears((prev) => {
          if (rows.length === 0 || prev.size > 0) return prev;
          const firstYear = [
            ...new Set(
              rows
                .map((row) => Number(row.year))
                .filter((y) => Number.isFinite(y)),
            ),
          ].sort((a, b) => b - a)[0];
          return firstYear ? new Set([firstYear]) : prev;
        });
      }
    } catch (err) {
      setError("Failed to fetch field application labor methods");
      console.error("Error fetching labor methods:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemSettings();
    fetchLaborMethods();
  }, [fetchLaborMethods]);

  const fetchApplicationTypeOptions = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: {
          list_key: SYSTEM_LIST_KEYS.applicationType,
          limit: 500,
        },
      });
      const rows = response.data?.data || [];
      setApplicationTypeOptions(
        rows.map((row) => String(row.value || "").trim()).filter(Boolean),
      );
    } catch (err) {
      console.error("Error fetching Application Type system list:", err);
      setApplicationTypeOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchApplicationTypeOptions();
  }, [fetchApplicationTypeOptions]);

  const mergedApplicationTypeOptions = useMemo(() => {
    const seen = new Set();
    const merged = [];

    (applicationTypeOptions || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((value) => {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(value);
        }
      });

    (laborMethods || [])
      .map((row) => String(row.applicationType || "").trim())
      .filter(Boolean)
      .forEach((value) => {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(value);
        }
      });

    return merged;
  }, [applicationTypeOptions, laborMethods]);

  const handleAddApplicationTypeOption = async (newValue) => {
    try {
      await api.post("/system-lists", {
        list_key: SYSTEM_LIST_KEYS.applicationType,
        list: "Application Type",
        value: newValue,
        is_active: true,
      });
      await fetchApplicationTypeOptions();
      return true;
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to add Application Type option.",
      );
      return false;
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await api.get("/settings/categories");
      if (response.data.success) {
        const settings = response.data.data;
        const currencyUnits = settings.currency_units || {};
        const defaultCurrency = currencyUnits.default_currency?.value || "CAD";
        const preferredUnit = currencyUnits.preferred_unit?.value || "m²";
        const unitOfMeasure =
          preferredUnit.toLowerCase().includes("m²") ||
          preferredUnit.toLowerCase().includes("m2")
            ? "sqm"
            : "sqft";

        setSystemSettings({
          defaultCurrency,
          preferredUnit: unitOfMeasure,
        });

        setFormData((prev) => ({
          ...prev,
          currency: defaultCurrency,
          unit_of_measure: unitOfMeasure,
        }));
      }
    } catch (err) {
      console.error("Error fetching system settings:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      application_type: "",
      cost_per_unit: "",
      unit_of_measure: systemSettings.preferredUnit || "sqm",
      year: new Date().getFullYear(),
      currency: systemSettings.defaultCurrency || "CAD",
      is_active: true,
    });
    setEditingMethod(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMethod) {
        await api.put(`/field-application-labor/${editingMethod.id}`, formData);
      } else {
        await api.post("/field-application-labor", formData);
      }

      setShowModal(false);
      resetForm();
      fetchLaborMethods();
    } catch (err) {
      setError("Failed to save field application labor method");
      console.error("Error saving labor method:", err);
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({
      application_type: method.applicationType,
      cost_per_unit:
        method.costPerUnit !== undefined
          ? method.costPerUnit
          : method.costPerUnitCad || 0,
      unit_of_measure:
        method.unitOfMeasure ||
        (method.unitType
          ? method.unitType.toLowerCase().includes("m²") ||
            method.unitType.toLowerCase().includes("m2")
            ? "sqm"
            : "sqft"
          : "sqm"),
      year: method.year,
      currency: method.currency || "CAD",
      is_active: method.isActive !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this field application labor method?",
      )
    )
      return;
    try {
      await api.delete(`/field-application-labor/${id}`);
      fetchLaborMethods();
    } catch (err) {
      setError("Failed to delete field application labor method");
      console.error("Error deleting labor method:", err);
    }
  };

  const handleDeleteLaborYear = async (year) => {
    const yearGroup = yearGroups.find((g) => g.year === year);
    const rows = (yearGroup && yearGroup.rows) || [];
    if (!rows.length) {
      alert(`No field application labor methods found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${rows.length} field application labor method(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        rows.map((method) =>
          api.delete(`/field-application-labor/${method.id}`),
        ),
      );
      fetchLaborMethods();
    } catch (err) {
      setError("Failed to delete field application labor methods for year");
      console.error("Error deleting labor methods for year:", err);
    }
  };

  const handleToggleActive = async (method) => {
    try {
      await api.put(`/field-application-labor/${method.id}`, {
        is_active: !method.isActive,
      });
      fetchLaborMethods();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const groupByYear = (rows = []) => {
    const grouped = new Map();
    rows.forEach((row) => {
      const year = Number(row.year);
      if (!Number.isFinite(year)) return;
      if (!grouped.has(year)) grouped.set(year, { year, rows: [] });
      grouped.get(year).rows.push(row);
    });
    return [...grouped.values()]
      .sort((a, b) => b.year - a.year)
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) =>
          String(a.applicationType || "").localeCompare(
            String(b.applicationType || ""),
          ),
        ),
      }));
  };

  const filteredMethods = useMemo(() => {
    return laborMethods.filter((method) => {
      const searchMatch =
        !searchTerm ||
        (method.applicationType || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const statusMatch =
        statusFilter === "" ||
        String(method.isActive !== false) === statusFilter;
      return searchMatch && statusMatch;
    });
  }, [laborMethods, searchTerm, statusFilter]);

  const yearGroups = useMemo(
    () => groupByYear(filteredMethods),
    [filteredMethods],
  );
  const allYears = useMemo(
    () =>
      [
        ...new Set(
          laborMethods
            .map((row) => Number(row.year))
            .filter((y) => Number.isFinite(y)),
        ),
      ].sort((a, b) => b - a),
    [laborMethods],
  );

  const toggleYear = (year) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const convertRowToPayload = (row, targetYear) => ({
    application_type: row.applicationType,
    cost_per_unit:
      row.costPerUnit !== undefined ? row.costPerUnit : row.costPerUnitCad || 0,
    unit_of_measure: row.unitOfMeasure || "sqm",
    year: targetYear,
    currency: row.currency || "CAD",
    is_active: row.isActive !== false,
  });

  const copyRowsToYear = async (sourceYear, targetYear) => {
    const fromYear = Number(sourceYear);
    const toYear = Number(targetYear);
    if (!Number.isFinite(fromYear) || !Number.isFinite(toYear)) return;

    const sourceRows = laborMethods.filter(
      (row) => Number(row.year) === fromYear,
    );
    if (sourceRows.length === 0) {
      setError(`No data found for source year ${fromYear}`);
      return;
    }

    const targetRows = laborMethods.filter(
      (row) => Number(row.year) === toYear,
    );
    if (targetRows.length > 0) {
      setError(
        `Target year ${toYear} already has records. Delete those rows first to avoid duplicates.`,
      );
      return;
    }

    setCopying(true);
    setCopyError("");
    try {
      await Promise.all(
        sourceRows.map((row) =>
          api.post(
            "/field-application-labor",
            convertRowToPayload(row, toYear),
          ),
        ),
      );
      setShowCopyModal(false);
      setCopySourceYear(null);
      setCopyTargetYear("");
      fetchLaborMethods();
      setExpandedYears((prev) => {
        const next = new Set(prev);
        next.add(toYear);
        return next;
      });
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to copy records to target year";
      setCopyError(message);
    } finally {
      setCopying(false);
    }
  };

  const handleImportPreviousYear = async (targetYear) => {
    const sortedOlderYears = allYears
      .filter((year) => year < targetYear)
      .sort((a, b) => b - a);
    const previousYear = sortedOlderYears[0];
    if (!previousYear) {
      setError(`No previous year data found to import into ${targetYear}`);
      return;
    }

    if (!window.confirm(`Import data from ${previousYear} into ${targetYear}?`))
      return;
    await copyRowsToYear(previousYear, targetYear);
  };

  const openCopyModal = (sourceYear) => {
    setCopySourceYear(sourceYear);
    setCopyTargetYear(String(Number(sourceYear) + 1));
    setCopyError("");
    setShowCopyModal(true);
  };

  const getApplicationTypeColor = (method) => {
    const colors = {
      Spray: "bg-blue-100 text-blue-800",
      Brush: "bg-green-100 text-green-800",
      Roller: "bg-purple-100 text-purple-800",
      Default: "bg-gray-100 text-gray-800",
    };
    return colors[method] || "bg-orange-100 text-orange-800";
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage field application labor methods and their costs for bridge
          maintenance
        </p>
        <InlineFieldTooltip
          page="field_application_labor"
          section="page_description"
          fieldKey="field_application_labor_overview"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search application types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button
          onClick={() => {
            setSearchTerm("");
            setStatusFilter("");
          }}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
        >
          Clear Filters
        </button>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add Method
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading labor methods...</p>
          </div>
        ) : yearGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No field application labor records found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rows
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {yearGroups.map((yearGroup) => {
                  const isExpanded = expandedYears.has(yearGroup.year);
                  return (
                    <React.Fragment key={yearGroup.year}>
                      <tr className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            onClick={() => toggleYear(yearGroup.year)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {yearGroup.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {yearGroup.rows.length}
                        </td>
                        <td
                          className="px-6 py-4 text-sm text-gray-500"
                          colSpan={3}
                        >
                          Year group
                        </td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleImportPreviousYear(yearGroup.year)
                              }
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                            >
                              Import
                            </button>
                            <button
                              type="button"
                              onClick={() => openCopyModal(yearGroup.year)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteLaborYear(yearGroup.year)}
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all field application labor methods for ${yearGroup.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded &&
                        yearGroup.rows.map((method) => (
                          <tr key={method.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4"></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {method.year}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              -
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <div className="flex items-center">
                                <FontAwesomeIcon
                                  icon={faWrench}
                                  className="h-4 w-4 text-gray-400 mr-2"
                                />
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getApplicationTypeColor(method.applicationType)}`}
                                >
                                  {method.applicationType}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {parseFloat(
                                method.costPerUnit !== undefined
                                  ? method.costPerUnit
                                  : method.costPerUnitCad || 0,
                              ).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {method.currency || "CAD"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {method.unitOfMeasure === "sqm"
                                ? "m²"
                                : method.unitOfMeasure === "sqft"
                                  ? "ft²"
                                  : method.unitOfMeasure ||
                                    method.unitType ||
                                    "m²"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={method.isActive !== false}
                                onClick={() => handleToggleActive(method)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  method.isActive !== false
                                    ? "bg-blue-600"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    method.isActive !== false
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleEdit(method)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <FontAwesomeIcon
                                    icon={faPencil}
                                    className="h-4 w-4"
                                  />
                                </button>
                                <button
                                  onClick={() => handleDelete(method.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="h-4 w-4"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCopyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-3">
              <span>Copy Field Labor Rows To New Year</span>
              <InlineFieldTooltip
                page="field_application_labor"
                section="modal_titles"
                fieldKey="copy_to_year"
                fallbackText="Copy field application labor rows from one year to another."
                title="Copy Field Labor Rows"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Source year:{" "}
              <span className="font-semibold">{copySourceYear}</span>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyTargetYear}
              onChange={(e) => setCopyTargetYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {copyError && (
              <p className="mt-2 text-sm text-red-600">{copyError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopySourceYear(null);
                  setCopyTargetYear("");
                  setCopyError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => copyRowsToYear(copySourceYear, copyTargetYear)}
                disabled={copying}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {copying ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingMethod
                    ? "Edit Field Application Labor Method"
                    : "Add New Field Application Labor Method"}
                </span>
                <InlineFieldTooltip
                  page="field_application_labor"
                  section="modal_titles"
                  fieldKey="labor_method_form"
                  fallbackText="Create or edit a field application labor method."
                  title="Field Application Labor Method Form"
                />
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Type *
                  </label>
                  <SearchableAddDropdown
                    value={formData.application_type}
                    options={mergedApplicationTypeOptions}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        application_type: String(selected || "").trim(),
                      })
                    }
                    onAddOption={handleAddApplicationTypeOption}
                    placeholder="Select or add application type..."
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost per Unit{" "}
                    {formData.currency && formData.unit_of_measure
                      ? `(${formData.currency}/${formData.unit_of_measure === "sqm" ? "m²" : "ft²"})`
                      : ""}{" "}
                    *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.cost_per_unit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cost_per_unit: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency *
                  </label>
                  <select
                    required
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit of Measure *
                  </label>
                  <select
                    required
                    value={formData.unit_of_measure}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        unit_of_measure: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sqm">sqm (m²)</option>
                    <option value="sqft">sqft (ft²)</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        year:
                          parseInt(e.target.value, 10) ||
                          new Date().getFullYear(),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2024"
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingMethod ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldApplicationLaborManagement;
